import { AxiosError } from 'axios';
import { Editor } from 'ckeditor5';
import { MouseEvent } from 'react';
import { QualioDocument, QualioDocumentSection } from '../api/model/document';
import { EditorName } from '../config/handlers';
import i18n from '../i18n/config';
import { navigateToSection } from './DocumentNavigationUtils';

const attachmentsDoNotExist = (msg?: string) =>
  msg && msg.indexOf('do not exist') > -1;

const attachmentsDoNotHavePermissions = (msg?: string) =>
  msg && msg.indexOf('permission') > -1;

const buildAttachmentsDoNotExistMsg = (
  section: QualioDocumentSection | undefined,
  msg?: string,
) => {
  if (!msg || !section) {
    return '';
  }
  const msgParts = msg.split('content');

  return i18n.t('errors.attachmentSectionDoesNotExist', {
    prefix: msgParts[0],
    sectionTitle: section.title,
  });
};

const buildMissingAttachmentsPermissionsMsg = (
  section: QualioDocumentSection | undefined,
  msg?: string,
) => {
  if (!msg || !section) {
    return '';
  }
  const msgParts = msg.split('referenced');

  return i18n.t('errors.attachmentsMissingPermissionsInSection', {
    prefix: msgParts[0],
    sectionTitle: section.title,
  });
};

const buildGenericAttachmentsErrorMsg = (
  section: QualioDocumentSection | undefined,
) => {
  if (!section) {
    return '';
  }

  return i18n.t('errors.issueWithAttachmentsInSection', {
    sectionTitle: section.title,
  });
};

export const buildAttachmentsErrorMessage = (
  msg?: string,
  sectionEditor?: Editor,
  doc?: QualioDocument,
) => {
  const sectionId = sectionEditor?.config.get('sectionId');
  const section = doc?.sections.reduce((s, sec, idx) => {
    if (sec.id === sectionId) {
      return { ...sec, title: `${idx + 1}. ${sec.title}` };
    } else {
      return s;
    }
  }, {} as QualioDocumentSection);

  let newMsg;

  if (attachmentsDoNotExist(msg)) {
    newMsg = buildAttachmentsDoNotExistMsg(section, msg);
  } else if (attachmentsDoNotHavePermissions(msg)) {
    newMsg = buildMissingAttachmentsPermissionsMsg(section, msg);
  } else {
    newMsg = buildGenericAttachmentsErrorMsg(section);
  }

  return newMsg;
};

export const isAttachmentError = (msg?: string) => {
  return msg?.length && msg?.indexOf('attachment') > -1;
};

export const buildToastDescription = (
  msg?: string | undefined | null,
  editorName: EditorName = 'documentEditorContent',
) => {
  if (!msg || !msg.length || msg.indexOf('section-link') === -1) {
    return msg;
  }
  const sectionLinkStartTag = '<section-link>';
  const sectionLinkEndTag = '</section-link>';
  const startLink = msg.indexOf(sectionLinkStartTag);
  const endLink = msg.indexOf(sectionLinkEndTag);
  const sectionTitle = msg.substring(
    startLink + sectionLinkStartTag.length,
    endLink,
  );

  const clickHandler = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    navigateToSection(Number.parseInt(sectionTitle[0]) - 1, editorName);
  };

  return (
    <>
      {`${msg.substring(0, startLink)}`}
      <button
        type="button"
        role="link"
        style={{ color: '#0069ff' }}
        onClick={clickHandler}
      >
        {sectionTitle}
      </button>
      {` ${msg.substring(endLink + sectionLinkEndTag.length + 1)}`}.{' '}
      <b>{i18n.t('messages.contentWillNotBeSaved')}</b>
    </>
  );
};

export const getHttpError = (
  e: unknown,
  defaultMessage = i18n.t('errors.somethingWentWrong'),
) => {
  if (
    e instanceof AxiosError &&
    e.response?.data.message &&
    typeof e.response?.data.message === 'string'
  ) {
    return new Error(e.response?.data.message);
  }
  if (e instanceof Error) {
    return e;
  }
  return new Error(defaultMessage);
};
